import { motion } from "framer-motion";
import { Check, Crown, Sparkles, Users, Briefcase } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "+966540423401";

const FLEET_COPY = {
  en: {
    eyebrow: "Fleet",
    title: "Every BlackRide class, from executive transfers to ultra-premium arrivals",
    cta: "Book This Car",
    ultraTitle: "Ultra Premium Collection",
    ultraSubtitle: "For weddings, diplomatic arrivals, celebrity transport, and once-in-a-lifetime moments.",
    ultraCta: "Request Ultra Premium",
    standardVehicles: [
      {
        id: "first-class",
        name: "BMW 735i",
        category: "First Class",
        description: "Signature luxury sedan for VIP and executive transfers.",
        seats: "3 passengers",
        luggage: "2 bags",
        features: ["Premium comfort", "Professional chauffeur"],
        image: "/fleet/first-class.png",
      },
      {
        id: "elite-suv",
        name: "GMC Yukon XL",
        category: "Elite SUV",
        description: "Spacious premium SUV for families, airport arrivals, and groups.",
        seats: "6-7 passengers",
        luggage: "6 bags",
        features: ["Spacious interiors", "Luxury experience"],
        image: "/fleet/elite-suv.png",
      },
      {
        id: "business",
        name: "Lexus ES / Ford Taurus",
        category: "Business",
        description: "Refined comfort for business travel and city transfers.",
        seats: "3 passengers",
        luggage: "2 bags",
        features: ["Executive comfort", "Smooth city travel"],
        image: "/fleet/business.png",
      },
      {
        id: "executive",
        name: "Mercedes Vito / Staria",
        category: "Executive",
        description: "Executive van option for family and corporate movement.",
        seats: "6-7 passengers",
        luggage: "4 bags",
        features: ["Comfortable long-distance travel", "Ideal for airport pickups"],
        image: "/fleet/executive.png",
      },
      {
        id: "group",
        name: "Toyota Hiace",
        category: "Group",
        description: "Reliable large-capacity option for pilgrim and group transport.",
        seats: "10-12 passengers",
        luggage: "8 bags",
        features: ["Large group comfort", "Excellent luggage space"],
        image: "/fleet/group.png",
      },
      {
        id: "standard",
        name: "Toyota Camry",
        category: "Standard",
        description: "Comfortable private transfer for everyday premium rides.",
        seats: "4 passengers",
        luggage: "2 bags",
        features: ["Value private ride", "Comfortable cabin"],
        image: "/fleet/standard.png",
      },
    ],
    ultraVehicles: [
      {
        id: "rolls-royce",
        name: "Rolls-Royce",
        category: "Ultra Premium",
        description: "For VIP ceremonies, diplomatic arrivals, and signature luxury experiences.",
        features: ["White-glove chauffeur coordination", "Event-ready arrival presence"],
      },
      {
        id: "maybach",
        name: "Mercedes-Maybach",
        category: "Ultra Premium",
        description: "An elite executive option for board-level travel and discreet luxury.",
        features: ["Executive privacy", "Luxury rear cabin experience"],
      },
      {
        id: "limousine",
        name: "Stretch Limousine",
        category: "Ultra Premium",
        description: "For weddings, celebrations, media appearances, and special VIP nights.",
        features: ["Special-occasion statement ride", "Custom event requests available"],
      },
    ],
  },
  ar: {
    eyebrow: "الأسطول",
    title: "جميع فئات بلاك رايد، من التنقلات التنفيذية إلى الوصول فائق الفخامة",
    cta: "احجز هذه المركبة",
    ultraTitle: "مجموعة الترا بريميوم",
    ultraSubtitle: "للأعراس، والاستقبالات الدبلوماسية، ونقل كبار الشخصيات، والمناسبات الاستثنائية.",
    ultraCta: "اطلب فئة الترا بريميوم",
    standardVehicles: [
      {
        id: "first-class",
        name: "BMW 735i",
        category: "الدرجة الأولى",
        description: "سيدان فاخرة مميزة لرحلات كبار الشخصيات والتنفيذيين.",
        seats: "3 ركاب",
        luggage: "2 حقائب",
        features: ["راحة فائقة", "سائق محترف"],
        image: "/fleet/first-class.png",
      },
      {
        id: "elite-suv",
        name: "GMC Yukon XL",
        category: "SUV فاخر",
        description: "سيارة SUV فاخرة وواسعة للعائلات والوصول من المطار والمجموعات.",
        seats: "6-7 ركاب",
        luggage: "6 حقائب",
        features: ["مساحة داخلية واسعة", "تجربة فاخرة"],
        image: "/fleet/elite-suv.png",
      },
      {
        id: "business",
        name: "Lexus ES / Ford Taurus",
        category: "الأعمال",
        description: "راحة راقية لسفر الأعمال والتنقلات داخل المدينة.",
        seats: "3 ركاب",
        luggage: "2 حقائب",
        features: ["راحة تنفيذية", "تنقل سلس داخل المدينة"],
        image: "/fleet/business.png",
      },
      {
        id: "executive",
        name: "Mercedes Vito / Staria",
        category: "تنفيذي",
        description: "فان تنفيذي مثالي للعائلات والتنقلات المؤسسية.",
        seats: "6-7 ركاب",
        luggage: "4 حقائب",
        features: ["راحة للرحلات الطويلة", "مثالي لاستقبال المطار"],
        image: "/fleet/executive.png",
      },
      {
        id: "group",
        name: "Toyota Hiace",
        category: "المجموعة",
        description: "خيار عملي ومريح للمجموعات الكبيرة والنقل الجماعي.",
        seats: "10-12 ركاب",
        luggage: "8 حقائب",
        features: ["راحة للمجموعات الكبيرة", "مساحة ممتازة للأمتعة"],
        image: "/fleet/group.png",
      },
      {
        id: "standard",
        name: "Toyota Camry",
        category: "العادي",
        description: "تنقل خاص مريح للرحلات اليومية الراقية.",
        seats: "4 ركاب",
        luggage: "2 حقائب",
        features: ["رحلة خاصة اقتصادية", "مقصورة مريحة"],
        image: "/fleet/standard.png",
      },
    ],
    ultraVehicles: [
      {
        id: "rolls-royce",
        name: "Rolls-Royce",
        category: "الترا بريميوم",
        description: "للأعراس والاستقبالات الدبلوماسية وتجارب الوصول الفاخرة جداً.",
        features: ["تنسيق سائق رفيع المستوى", "وصول مهيب للمناسبات"],
      },
      {
        id: "maybach",
        name: "Mercedes-Maybach",
        category: "الترا بريميوم",
        description: "خيار تنفيذي راقٍ لرحلات كبار التنفيذيين والرفاهية الهادئة.",
        features: ["خصوصية تنفيذية", "تجربة فاخرة للمقاعد الخلفية"],
      },
      {
        id: "limousine",
        name: "Stretch Limousine",
        category: "الترا بريميوم",
        description: "للأعراس والاحتفالات والظهور الإعلامي والمناسبات الخاصة.",
        features: ["رحلة مميزة للمناسبات", "طلبات مناسبات مخصصة"],
      },
    ],
  },
};

function getWhatsAppLink(vehicleName) {
  const message = encodeURIComponent(`Hello BlackRide! I'd like to book the ${vehicleName}. Please confirm availability.`);
  return `https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, "")}?text=${message}`;
}

export default function VehicleClasses() {
  const { lang } = useLanguage();
  const copy = FLEET_COPY[lang] || FLEET_COPY.en;

  return (
    <section id="fleet" data-testid="vehicle-classes-section" className="relative py-20 md:py-24 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-4">{copy.eyebrow}</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight max-w-4xl">
            {copy.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {copy.standardVehicles.map((vehicle, index) => (
            <motion.article
              key={vehicle.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: index * 0.06 }}
              className="group rounded-[28px] border border-white/8 bg-[#111] overflow-hidden hover:-translate-y-1 transition-all duration-300"
            >
              <div className="relative h-72 bg-[radial-gradient(circle_at_top,#2a2a2a,#0b0b0b_68%)] p-6 md:p-7 overflow-hidden">
                <div className="absolute inset-x-12 bottom-6 h-10 rounded-full bg-black/55 blur-2xl opacity-80" />
                <div className="relative h-full w-full overflow-hidden rounded-[22px] border border-white/8 bg-[#0d0d0d] shadow-[0_22px_40px_rgba(0,0,0,0.32)]">
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),transparent_22%,rgba(0,0,0,0.18))] z-10 pointer-events-none" />
                  <img
                    src={vehicle.image}
                    alt={`${vehicle.name} ${vehicle.category} chauffeur car`}
                    className="h-full w-full object-cover object-center group-hover:scale-[1.035] transition-transform duration-700 ease-out"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="p-6">
                <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-3">{vehicle.category}</p>
                <h3 className="font-['Playfair_Display'] text-2xl text-white mb-2">{vehicle.name}</h3>
                <p className="text-gray-400 mb-5">{vehicle.description}</p>
                <div className="flex flex-wrap items-center gap-6 text-sm text-gray-300 mb-5">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#C7A239]" />
                    <span>{vehicle.seats}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[#C7A239]" />
                    <span>{vehicle.luggage}</span>
                  </div>
                </div>
                <ul className="space-y-2 mb-6 text-sm text-gray-300">
                  {vehicle.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#C7A239] mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={getWhatsAppLink(vehicle.name)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center w-full bg-[#C7A239] hover:bg-[#D4B34A] text-black font-medium px-5 py-3 rounded-full transition"
                  data-testid={`fleet-cta-${vehicle.id}`}
                  data-track="fleet_cta_click"
                >
                  {copy.cta}
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 rounded-[32px] border border-[#C7A239]/15 bg-[radial-gradient(circle_at_top,#241b09,transparent_38%),#0d0d0d] p-8 md:p-10">
          <div className="max-w-3xl mb-10">
            <div className="w-14 h-14 rounded-2xl bg-[#C7A239]/10 flex items-center justify-center mb-5">
              <Crown className="w-7 h-7 text-[#C7A239]" />
            </div>
            <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-4">{copy.ultraTitle}</h3>
            <p className="text-gray-300 text-base leading-relaxed">{copy.ultraSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {copy.ultraVehicles.map((vehicle, index) => (
              <motion.article
                key={vehicle.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="rounded-[28px] border border-white/10 bg-white/[0.03] p-7"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#C7A239]/10 flex items-center justify-center mb-5">
                  <Sparkles className="w-5 h-5 text-[#C7A239]" />
                </div>
                <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-3">{vehicle.category}</p>
                <h4 className="font-['Playfair_Display'] text-2xl text-white mb-3">{vehicle.name}</h4>
                <p className="text-gray-400 mb-5 leading-relaxed">{vehicle.description}</p>
                <ul className="space-y-2 mb-6 text-sm text-gray-300">
                  {vehicle.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#C7A239] mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={getWhatsAppLink(vehicle.name)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center w-full border border-[#C7A239]/40 hover:border-[#C7A239] text-white hover:text-[#C7A239] font-medium px-5 py-3 rounded-full transition"
                  data-testid={`fleet-cta-${vehicle.id}`}
                  data-track="fleet_cta_click"
                >
                  {copy.ultraCta}
                </a>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
